import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Scale, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import WhatsAppWidget from "../components/WhatsAppWidget";
import SEO from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CaseWatchDetailPage() {
  const { id } = useParams();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    axios.get(`${API}/case-watch/${id}`)
      .then((res) => setCaseData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="pt-32 flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-gold" />
        </div>
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="pt-32 text-center py-20">
          <p className="text-charcoal/60 text-lg">Case not found.</p>
          <Link to="/case-watch" className="text-brand-gold mt-4 inline-block">Back to Case Watch</Link>
        </div>
      </div>
    );
  }

  const sections = [
    { label: "The Practical Question", content: caseData.practical_question, icon: "?" },
    { label: "What Happened", content: caseData.what_happened, icon: "i" },
    { label: "What the Court Decided", content: caseData.court_decision, icon: Scale },
    { label: "Why It Matters", content: caseData.why_it_matters, icon: "!" },
    { label: "Actions Clients Should Consider", content: caseData.actions_to_consider, icon: AlertTriangle },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`${caseData.title} | Case Watch`}
        description={caseData.practical_question}
        url={`https://mhesheattorneys.co.za/case-watch/${id}`}
      />
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-12 bg-charcoal">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/case-watch" className="inline-flex items-center text-brand-gold text-sm mb-6 hover:text-white transition-colors">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Case Watch
          </Link>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="flex items-center gap-3 text-white/50 mb-4">
              <Calendar className="h-4 w-4" />
              <span className="text-sm">{caseData.judgment_date}</span>
              <span className="text-brand-gold">|</span>
              <span className="text-xs text-brand-gold uppercase">{caseData.related_service}</span>
            </div>
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-white tracking-tighter">
              {caseData.title}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Video */}
      {caseData.video_url && (
        <section className="py-8 bg-light-grey">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="aspect-video">
              <iframe
                src={caseData.video_url}
                title={caseData.title}
                className="w-full h-full"
                allowFullScreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              />
            </div>
          </div>
        </section>
      )}

      {/* Content Sections */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-10">
            {sections.map((section, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: idx * 0.05 }}
                className="border-l-4 border-brand-gold pl-6"
              >
                <h2 className="font-serif text-xl text-charcoal mb-3">{section.label}</h2>
                <p className="text-charcoal/70 font-sans leading-relaxed whitespace-pre-line">
                  {section.content}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Source */}
          <div className="mt-12 pt-8 border-t border-charcoal/10">
            <p className="text-charcoal/50 text-sm font-sans">
              <strong>Source:</strong> {caseData.source}
            </p>
          </div>

          {/* Service Invitation */}
          <div className="mt-8 p-6 bg-light-grey border border-charcoal/10">
            <p className="text-charcoal/70 font-sans text-sm mb-4">
              Need advice on how this judgment affects your {caseData.related_service.toLowerCase()} matters?
            </p>
            <Link to="/#lead-capture">
              <Button className="btn-gold px-6 py-3 text-sm font-semibold uppercase tracking-wider rounded-none">
                Discuss With Our Team
              </Button>
            </Link>
          </div>

          {/* Disclaimer */}
          <div className="mt-8 p-4 bg-charcoal/5 border border-charcoal/10">
            <p className="text-charcoal/40 font-sans text-xs leading-relaxed">
              <strong>Legal Information Disclaimer:</strong> This summary is provided for general informational purposes only and does not constitute legal advice. The analysis reflects the judgment as publicly reported and should not be relied upon as a substitute for independent legal counsel. Mheshe Attorneys accepts no liability for actions taken or not taken based on this summary. Please consult a qualified legal professional for advice specific to your circumstances.
            </p>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppWidget />
    </div>
  );
}
